// src/components/Settings.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import Theme from "./Theme";
import FontSize from "./FontSize";
import AudioPlayerDisable from "./AudioPlayerDisable";
import RecitationList from "./RecitationList";
import { useAudioContext } from "../contexts/AudioContext";
import TranslationSelector from "./TranslationSelector";
import { FaTimes } from "react-icons/fa";
import { storageManager } from "../utils/storageManager";
import {
  APP_VERSION_LABEL,
  APP_COMMIT_COUNT,
} from "../config/appVersion";

const Settings = React.memo(function Settings({ isOpen, onClose }) {
  const settingsRef = useRef(null);
  const { recitationId, changeRecitationId } = useAudioContext();
  const [storageHealth, setStorageHealth] = useState(() =>
    storageManager.getStorageHealth()
  );
  const [cacheMsg, setCacheMsg] = useState("");

  const refreshStorageHealth = useCallback(() => {
    setStorageHealth(storageManager.getStorageHealth());
  }, []);

  useEffect(() => {
    if (isOpen) {
      refreshStorageHealth();
      setCacheMsg("");
    }
  }, [isOpen, refreshStorageHealth]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen, onClose]);

  const onFreeCache = () => {
    const result = storageManager.freeCacheSpace();
    refreshStorageHealth();
    const kb = Math.round(result.freedBytes / 1024);
    setCacheMsg(
      kb > 0
        ? `Freed about ${kb} KB. Bookmarks and settings were kept.`
        : "Cache was already clear. Bookmarks and settings were kept."
    );
  };

  return (
    <nav
      ref={settingsRef}
      className={`fixed top-0 right-0 h-screen w-80 bg-gray-900 text-white transform ${
        isOpen ? "translate-x-0" : "translate-x-full"
      } transition-transform duration-300 ease-in-out z-50`}
      aria-hidden={!isOpen}
    >
      <div className="p-4 flex justify-between items-center border-b border-gray-700">
        <h2 className="text-xl font-bold">Settings</h2>
        <button
          onClick={onClose}
          className="text-white focus:outline-none hover:text-teal-400 transition-colors duration-200"
          aria-label="Close settings panel"
        >
          <FaTimes className="w-6 h-6" />
        </button>
      </div>

      <div className="h-full overflow-y-auto bg-gray-900">
        <div className="p-4 space-y-6 pb-24">
          <section>
            <div className="flex justify-between items-center gap-3 mb-4">
              <h3 className="text-lg font-semibold mb-4">Display</h3>
              <Theme />
            </div>
            <div className="space-y-4">
              <FontSize />
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-4">Translation</h3>
            <div className="space-y-4">
              <TranslationSelector />
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-4">Audio</h3>
            <div className="space-y-4">
              <AudioPlayerDisable />
              <RecitationList
                selectedRecitation={recitationId}
                onRecitationChange={changeRecitationId}
              />
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Storage</h3>
            <p className="text-sm text-gray-400 mb-3">
              Cached data usage: {storageHealth.percentage}%
              {storageHealth.status !== "healthy"
                ? ` — ${storageHealth.message}`
                : ""}
            </p>
            <button
              type="button"
              onClick={onFreeCache}
              className="rounded-lg bg-teal-700 px-3 py-2 text-sm font-medium hover:bg-teal-600 transition-colors"
            >
              Free cache space
            </button>
            {cacheMsg ? (
              <p className="mt-2 text-xs text-teal-300">{cacheMsg}</p>
            ) : (
              <p className="mt-2 text-xs text-gray-500">
                Clears old download caches. Does not remove bookmarks or your
                settings.
              </p>
            )}
            <p className="mt-4 text-xs text-gray-500">
              App {APP_VERSION_LABEL}
              {APP_COMMIT_COUNT ? ` · build ${APP_COMMIT_COUNT}` : ""}
            </p>
          </section>
        </div>
      </div>
    </nav>
  );
});

export default Settings;
